import React, { useMemo, useState } from "react";
import { useGame, usePlayer, usePlayers } from "@empirica/core/player/classic/react";
import { Button } from "../components/Button";
import { useRandomizedQuizQuestions } from "../components/useRandomizedQuizQuestions";
import { TrackingClipExample } from "../components/TrackingClipExample";
import {
  formatCents,
  formatDollars,
  getTrackingRulesParams,
} from "../tracking/trackingParams";
import { getCounterpartCopy, isLossFrameDesign } from "../payoffFrame";
import { dollars } from "../lossFrameMoney";
import { usesDisclosedAssignedDifficulty } from "../../../shared/experimentBehavior.js";

function getQuizQuestions(
  lossFrame,
  counterpartLabel,
  discloseAssignedDifficulty,
  assignedStandardTask
) {
  const questions = [
    {
      id: "earningsMechanic",
      prompt: lossFrame
        ? "How do you prevent money from being lost in the Mouse Tracking Game?"
        : "How do you earn money in the Mouse Tracking Game?",
      options: [
        {
          value: "click",
          label: "Clicking on the moving circle.",
        },
        {
          value: "correct",
          label: "Keeping your mouse inside the moving circle.",
        },
        {
          value: "moving",
          label: "Moving the circle from one side of the page to the other.",
        },
      ],
      correctValue: "correct",
    },
    {
      id: "winnerTakesPot",
      prompt: lossFrame
        ? `If your ${counterpartLabel} keeps their cursor in the circle longer than you do, what happens to the money your team saved from being lost?`
        : `If your ${counterpartLabel} keeps their cursor in the circle longer than you do, how much of the shared pot will you get?`,
      options: lossFrame
        ? [
            {
              value: "half",
              label: "You keep half of the money your team saved.",
            },
            {
              value: "own",
              label: "You keep only the money that you saved from being lost.",
            },
            {
              value: "correct",
              label: `Your ${counterpartLabel} takes all of the money your team saved.`,
            },
          ]
        : [
            {
              value: "half",
              label: "Half of the shared pot.",
            },
            {
              value: "own",
              label: "Only the money that you added to the pot.",
            },
            {
              value: "correct",
              label: "None of the shared pot.",
            },
          ],
      correctValue: "correct",
    },
  ];

  if (discloseAssignedDifficulty) {
    questions.push({
      id: "taskDifficultyAssignment",
      prompt: "Which Game 1 tracking task were you assigned?",
      options: [
        {
          value: "standard",
          label: "The standard task, with the larger moving circle.",
        },
        {
          value: "hard",
          label: "The hard task, with the smaller moving circle.",
        },
        {
          value: "same",
          label: "Both participants were assigned the same task.",
        },
      ],
      correctValue: assignedStandardTask ? "standard" : "hard",
    });
  }

  return questions;
}

export function Stage1GameRulesStage() {
  const game = useGame();
  const player = usePlayer();
  const players = usePlayers();
  const [currentPage, setCurrentPage] = useState(
    player.stage.get("stage1RulesPage") === "quiz" ? "quiz" : "rules"
  );
  const [showExample, setShowExample] = useState(
    player.stage.get("stage1ShowExample") === true ||
      player.stage.get("stage1ClipPlayed") === true
  );
  const [hasPlayedClip, setHasPlayedClip] = useState(
    player.stage.get("stage1ClipPlayed") === true
  );
  const [exampleDifficulty, setExampleDifficulty] = useState(() => {
    const radiusMultiplier = Number(
      player.get("stage1CircleRadiusMultiplier") ?? 1
    );
    const assignedStandard =
      player.get("stage1TreatmentRole") === "winner" ||
      radiusMultiplier > 1;

    return assignedStandard ? "standard" : "hard";
  });
  const [answers, setAnswers] = useState(
    player.stage.get("stage1RulesQuizAnswers") ?? {}
  );
  const [submittedAnswers, setSubmittedAnswers] = useState(
    player.stage.get("stage1RulesQuizSubmittedAnswers") ?? {}
  );
  const [attemptedQuiz, setAttemptedQuiz] = useState(
    player.stage.get("stage1RulesQuizAttempted") === true
  );

  const allSubmitted = useMemo(
    () => players.every((p) => p.stage.get("submit")),
    [players]
  );
  const design = game.get("design") || {};
  const lossFrame = isLossFrameDesign(design);
  const counterpart = getCounterpartCopy(design);
  const discloseAssignedDifficulty = usesDisclosedAssignedDifficulty(design);
  const stage1RadiusMultiplier = Number(player.get("stage1CircleRadiusMultiplier") ?? 1);
  const assignedStandardTask =
    player.get("stage1TreatmentRole") === "winner" ||
    stage1RadiusMultiplier > 1;
  const questions = useMemo(
    () =>
      getQuizQuestions(
        lossFrame,
        counterpart.term,
        discloseAssignedDifficulty,
        assignedStandardTask
      ),
    [assignedStandardTask, counterpart.term, discloseAssignedDifficulty, lossFrame]
  );
  const quizQuestions = useRandomizedQuizQuestions(
    questions, player.stage, "stage1RulesQuizOptionOrder"
  );
  const incorrectQuestionIds = useMemo(
    () =>
      quizQuestions.filter(
        (question) => submittedAnswers[question.id] !== question.correctValue
      ).map((question) => question.id),
    [quizQuestions, submittedAnswers]
  );
  const allQuestionsAnswered = useMemo(
    () =>
      quizQuestions.every(
        (question) => typeof submittedAnswers[question.id] === "string"
      ),
    [quizQuestions, submittedAnswers]
  );
  const passedQuiz = attemptedQuiz && incorrectQuestionIds.length === 0;
  const trackingParams = getTrackingRulesParams(design, 1);

  function handleContinue() {
    player.stage.set("submit", true);
  }

  function handleShowExample() {
    if (!showExample) {
      setShowExample(true);
      player.stage.set("stage1ShowExample", true);
    }
  }

  function handleClipPlayed() {
    if (!hasPlayedClip) {
      setHasPlayedClip(true);
      player.stage.set("stage1ClipPlayed", true);
    }
  }

  function handleExampleDifficultyChange(difficulty) {
    setExampleDifficulty(difficulty);
  }

  function handleExampleDifficultyKeyDown(event) {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
      return;
    }

    event.preventDefault();
    const nextDifficulty =
      event.key === "ArrowLeft" || event.key === "Home" ? "standard" : "hard";
    setExampleDifficulty(nextDifficulty);
    event.currentTarget.parentElement
      ?.querySelector(`[data-example-difficulty="${nextDifficulty}"]`)
      ?.focus();
  }

  function handleContinueToQuiz() {
    setCurrentPage("quiz");
    player.stage.set("stage1RulesPage", "quiz");
  }

  const exampleRadiusMultiplier = discloseAssignedDifficulty
    ? exampleDifficulty === "standard"
      ? 1.33
      : 1
    : stage1RadiusMultiplier;

  function handleBackToRules() {
    setCurrentPage("rules");
    player.stage.set("stage1RulesPage", "rules");
  }

  function handleAnswerChange(questionId, value) {
    const nextAnswers = {
      ...answers,
      [questionId]: value,
    };
    setAnswers(nextAnswers);
    player.stage.set("stage1RulesQuizAnswers", nextAnswers);
  }

  function handleSubmitQuiz() {
    const nextCount = (player.stage.get("stage1RulesQuizAttemptCount") ?? 0) + 1;
    setSubmittedAnswers(answers);
    setAttemptedQuiz(true);
    // Keep repeated submissions distinct in Empirica's attribute history.
    player.stage.set("stage1RulesQuizSubmittedAnswers", { ...answers, _attempt: nextCount });
    player.stage.set("stage1RulesQuizAttempted", true);
    player.stage.set("stage1RulesQuizAttemptCount", nextCount);
  }

  return (
    <div className="py-10 max-w-4xl mx-auto px-8 space-y-8">
      {currentPage === "rules" ? (
        <>
          <p className="text-5xl font-medium text-gray-900">Mouse Tracking Game</p>
          {lossFrame ? (
            <section className="space-y-3">
              <div className="text-2xl font-semibold uppercase tracking-wide text-gray-700">
                Money at stake this game
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-md border-2 border-gray-900 bg-white p-4 shadow-sm">
                  <div className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                    You have
                  </div>
                  <div className="mt-2 text-4xl font-semibold text-gray-900">
                    {dollars(trackingParams.maxPerParticipant)}
                  </div>
                </div>
                <div className="rounded-md border-2 border-gray-900 bg-white p-4 shadow-sm">
                  <div className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                    {counterpart.capitalizedTerm} has
                  </div>
                  <div className="mt-2 text-4xl font-semibold text-gray-900">
                    {dollars(trackingParams.maxPerParticipant)}
                  </div>
                </div>
              </div>
            </section>
          ) : null}
          <div className="rounded-md border border-gray-200 p-5 space-y-3 text-2xl text-gray-700">
            <p>
              A circle moves around the screen randomly for <b>{trackingParams.durationSeconds} seconds</b>.
            </p>

            <p>
              {lossFrame ? (
                <>
                  At the start of the game, <b>${formatDollars(trackingParams.maxPerParticipant)}</b>{" "}
                  is at risk. You <b><u>lose</u> {formatCents(trackingParams.centsPerSecond)} cents </b> each second{" "}
                  your <b>cursor is <u>outside</u> the circle</b>.
                </>
              ) : (
                <>
                  You earn <b>{formatCents(trackingParams.centsPerSecond)} cents each second</b>{" "}
                  your <b>cursor is inside the circle</b>, up to ${formatDollars(trackingParams.maxPerParticipant)}.
                </>
              )}
            </p>

            <p>
              {lossFrame ? (
                <>
                </>
              ) : (
                <>
                  Your earnings contribute to a <b>pot of money <u>shared</u></b> with your {counterpart.term}.
                </>
              )}
            </p>

            <p>
              {lossFrame ? (
                <b>
                  Whoever keeps their cursor in the circle <u>longer</u> takes the money saved by <u>BOTH players</u>.
                </b>
              ) : (
                <b>Whoever keeps their cursor in the circle <u>longer</u> takes the whole pot as bonus pay.</b>
              )}
            </p>
          </div>

          {discloseAssignedDifficulty ? (
            <div
              className={`rounded-md border p-4 text-3xl font-semibold ${
                assignedStandardTask
                  ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                  : "border-amber-300 bg-amber-50 text-amber-950"
              }`}
            >
              <div>
                Your assigned difficulty: <b>{assignedStandardTask ? "Standard" : "Hard"}</b>
              </div>
              <div className="mt-2 text-2xl font-medium">
                {assignedStandardTask ? `${counterpart.capitalizedTerm}'s` : "Your"} target circle is smaller.
              </div>
            </div>
          ) : null}

          {!showExample ? (
            <div className="flex items-center space-x-4">
              <Button handleClick={handleShowExample} autoFocus>
                <p>Show example</p>
              </Button>
            </div>
          ) : null}

          {showExample ? (
            <>
              <div className="rounded-md border border-gray-200 p-5 text-2xl text-gray-700">
                <p className="font-medium text-gray-900">Circle Tracking Example</p>
                <div className="mt-3 space-y-3">
                  <p>
                    This is a limited <b>preview</b> of the game.
                  </p>

                  <p>
                    The circle will change from "😏" to "😫" when your mouse is over it.
                  </p>
                  {discloseAssignedDifficulty ? (
                    <div
                      className="inline-flex overflow-hidden rounded-md border border-gray-300 bg-white"
                      role="tablist"
                      aria-label="Example difficulty"
                    >
                      {["standard", "hard"].map((difficulty) => {
                        const selected = exampleDifficulty === difficulty;

                        return (
                          <button
                            key={difficulty}
                            type="button"
                            role="tab"
                            id={`example-${difficulty}-tab`}
                            aria-selected={selected}
                            aria-controls="tracking-example-panel"
                            tabIndex={selected ? 0 : -1}
                            data-example-difficulty={difficulty}
                            className={`min-w-36 px-5 py-3 text-xl font-semibold capitalize focus:outline-none focus:ring-2 focus:ring-inset focus:ring-empirica-500 ${
                              selected
                                ? "bg-gray-900 text-white"
                                : "bg-white text-gray-700 hover:bg-gray-50"
                            } ${difficulty === "hard" ? "border-l border-gray-300" : ""}`}
                            onClick={() => handleExampleDifficultyChange(difficulty)}
                            onKeyDown={handleExampleDifficultyKeyDown}
                          >
                            {difficulty}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                  {/* <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                    <li>There is NO time-inside-circle timer.</li>
                    <li>There is a timer counting down the remaining time in the game</li>
                  </ul> */}
                  <div
                    id="tracking-example-panel"
                    role={discloseAssignedDifficulty ? "tabpanel" : undefined}
                    aria-labelledby={
                      discloseAssignedDifficulty
                        ? `example-${exampleDifficulty}-tab`
                        : undefined
                    }
                  >
                    <TrackingClipExample
                      key={`tracking-example-${exampleDifficulty}`}
                      radiusMultiplier={exampleRadiusMultiplier}
                      dollarsPerSecond={trackingParams.dollarsPerSecond}
                      startingMoneyDollars={trackingParams.maxPerParticipant}
                      emojiMode={trackingParams.emojiMode}
                      lossFrame={lossFrame}
                      onClipPlayed={handleClipPlayed}
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <Button
                  handleClick={handleContinueToQuiz}
                  disabled={player.stage.get("submit") || !hasPlayedClip}
                >
                  <p>Continue to Quiz</p>
                </Button>
                {!hasPlayedClip ? (
                  <span className="text-sm text-gray-500">Play the example to continue.</span>
                ) : null}
              </div>
            </>
          ) : null}
        </>
      ) : (
        <>
          <div className="rounded-md border border-gray-200 p-5 space-y-6 text-[19px] text-gray-700">
            <div className="space-y-2">
              <h2 className="text-[23px] font-medium text-gray-900">
                Mouse Tracking Game Rules Check
              </h2>
              <p>
                Answer all questions correctly to confirm you understand how the
                Mouse Tracking Game works.
              </p>
            </div>

            <div className="space-y-6">
              {quizQuestions.map((question, index) => {
                const selectedValue = answers[question.id];
                const submittedValue = submittedAnswers[question.id];
                const showIncorrect =
                  attemptedQuiz &&
                  typeof submittedValue === "string" &&
                  submittedValue !== question.correctValue;
                const showCorrect =
                  attemptedQuiz && submittedValue === question.correctValue;

                return (
                  <div
                    key={question.id}
                    className={`rounded-md border p-4 space-y-3 ${
                      showIncorrect
                        ? "border-red-300 bg-red-50"
                        : showCorrect
                          ? "border-green-300 bg-green-50"
                          : "border-gray-200"
                    }`}
                  >
                    <p className="font-medium text-gray-900">
                      {index + 1}. {question.prompt}
                    </p>
                    <div className="space-y-2">
                      {question.options.map((option) => (
                        <label
                          key={option.value}
                          className="flex items-start space-x-3 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name={question.id}
                            value={option.value}
                            checked={selectedValue === option.value}
                            onChange={() =>
                              handleAnswerChange(question.id, option.value)
                            }
                          />
                          <span>{option.label}</span>
                        </label>
                      ))}
                    </div>
                    {showIncorrect ? (
                      <p className="text-[19px] text-red-600">
                        This answer is not correct. Re-read the rules and try again.
                      </p>
                    ) : null}
                    {showCorrect ? (
                      <p className="text-[19px] text-green-700">Correct.</p>
                    ) : null}
                  </div>
                );
              })}
            </div>

            {attemptedQuiz && !passedQuiz ? (
              <div className="rounded-md border border-red-200 bg-red-50 p-4 text-[19px] text-red-700">
                {allQuestionsAnswered
                  ? "Some answers are still incorrect. Update your choices and submit the quiz again."
                  : "Answer every question, then submit the quiz again."}
              </div>
            ) : null}

            {passedQuiz ? (
              <div className="rounded-md border border-green-200 bg-green-50 p-4 text-[19px] text-green-700">
                All answers are correct. You can continue.
              </div>
            ) : null}
          </div>

          <div className="flex items-center space-x-4">
            <Button handleClick={handleBackToRules}>
              <span className="text-[17px]">Back to Rules</span>
            </Button>
            <Button
              handleClick={handleSubmitQuiz}
              disabled={player.stage.get("submit")}
            >
              <span className="text-[17px]">Submit Quiz</span>
            </Button>
            <Button
              handleClick={handleContinue}
              autoFocus
              disabled={player.stage.get("submit") || !passedQuiz}
            >
              <p className="text-[17px]">Continue to Tracking Task</p>
            </Button>
            {player.stage.get("submit") && !allSubmitted && (
              <span className="text-[17px] text-gray-500">
                Waiting for the other participant…
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
